import { useEffect, useRef } from "react";
import { format } from "date-fns";
import {
  ISO_DATE,
  errorResult,
  getModelContext,
  registerMcpTool,
  type McpToolDescriptor,
} from "@/hooks/use-webmcp";

// WebMCP tool that lets an MCP-capable agent open and pre-fill the booking
// widget's date-inquiry dialog on the guest's behalf. It only DRIVES the
// existing dialog state (dates, guests, name, email, message) and opens it — it
// never POSTs to /api/inquiries. The human still reviews the pre-filled dialog
// and presses "Send inquiry" themselves.

const MAX_GUESTS = 12;

const dateInquiryInputSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    email: {
      type: "string",
      format: "email",
      description: "The guest's email address so the host can reply.",
    },
    name: {
      type: "string",
      description: "Optional — the guest's name.",
    },
    message: {
      type: "string",
      description:
        "Optional note for the host (flexible dates, special occasion, questions).",
    },
    checkIn: {
      type: "string",
      pattern: "^\\d{4}-\\d{2}-\\d{2}$",
      description: "Optional desired check-in date in ISO format (YYYY-MM-DD).",
    },
    checkOut: {
      type: "string",
      pattern: "^\\d{4}-\\d{2}-\\d{2}$",
      description:
        "Optional desired check-out date in ISO format (YYYY-MM-DD). Must be after checkIn.",
    },
    guests: {
      type: "integer",
      minimum: 1,
      maximum: MAX_GUESTS,
      description: "Optional number of guests staying (1-12).",
    },
  },
  required: ["email"],
} as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type DateInquiryMcpPrefill = {
  name?: string;
  email: string;
  message?: string;
  checkIn?: Date;
  checkOut?: Date;
  guests?: number;
};

export type DateInquiryMcpHandlers = {
  /** Pre-fill the inquiry dialog fields and open it (does not submit). */
  openWithPrefill: (prefill: DateInquiryMcpPrefill) => void;
};

/**
 * Registers a single WebMCP tool ("start_cabin_date_inquiry") that opens the
 * booking widget's date-inquiry dialog pre-filled with the guest's contact
 * details and desired dates. It stops before submitting so the human confirms
 * and sends. No-ops entirely for browsers without WebMCP, so there is no
 * behavior change for human visitors.
 */
export function useDateInquiryMcpTool(handlers: DateInquiryMcpHandlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const mc = getModelContext();
    if (!mc) return;

    const tool: McpToolDescriptor = {
      name: "start_cabin_date_inquiry",
      description:
        "Open and pre-fill the Legendary Log Cabin date-inquiry dialog with the guest's email, optional name, message, desired dates, and guest count. Use this when a guest's dates show unavailable or they want to ask the host about specific dates. This does NOT send the inquiry — it only opens and fills the on-page dialog so the guest can review and press \"Send inquiry\".",
      inputSchema: dateInquiryInputSchema as unknown as Record<string, unknown>,
      execute: async (args) => {
        const email = typeof args.email === "string" ? args.email.trim() : "";
        const name = typeof args.name === "string" ? args.name.trim() : "";
        const message = typeof args.message === "string" ? args.message.trim() : "";
        const checkIn = typeof args.checkIn === "string" ? args.checkIn : "";
        const checkOut = typeof args.checkOut === "string" ? args.checkOut : "";

        if (!EMAIL_RE.test(email)) {
          return errorResult("email must be a valid email address.");
        }

        let from: Date | undefined;
        let to: Date | undefined;
        // Dates are optional, but if either is supplied both must be valid.
        if (checkIn || checkOut) {
          if (!ISO_DATE.test(checkIn) || !ISO_DATE.test(checkOut)) {
            return errorResult(
              "To set dates, provide both checkIn and checkOut as ISO dates (YYYY-MM-DD).",
            );
          }
          from = new Date(checkIn + "T12:00:00");
          to = new Date(checkOut + "T12:00:00");
          if (isNaN(from.getTime()) || isNaN(to.getTime())) {
            return errorResult("checkIn / checkOut are not valid dates.");
          }
          if (to.getTime() <= from.getTime()) {
            return errorResult("checkOut must be after checkIn.");
          }
        }

        let guests: number | undefined;
        if (args.guests !== undefined) {
          const g = Math.floor(Number(args.guests));
          if (!Number.isFinite(g) || g < 1 || g > MAX_GUESTS) {
            return errorResult(`guests must be a whole number between 1 and ${MAX_GUESTS}.`);
          }
          guests = g;
        }

        handlersRef.current.openWithPrefill({
          name: name || undefined,
          email,
          message: message || undefined,
          checkIn: from,
          checkOut: to,
          guests,
        });

        const structured = {
          email,
          ...(name ? { name } : {}),
          ...(message ? { message } : {}),
          ...(from && to ? { checkIn, checkOut } : {}),
          ...(guests !== undefined ? { guests } : {}),
          submitted: false,
          note: "Inquiry dialog opened and pre-filled. Nothing was sent — the guest reviews and presses \"Send inquiry\".",
        };

        const datePhrase =
          from && to ? ` for ${format(from, "MMM d")}–${format(to, "MMM d, yyyy")}` : "";
        return {
          content: [
            {
              type: "text",
              text: `The date-inquiry dialog is now open and pre-filled for ${email}${datePhrase}. Nothing has been sent yet — the guest still needs to review and press "Send inquiry".`,
            },
          ],
          structuredContent: structured,
        };
      },
    };

    return registerMcpTool(tool);
  }, []);
}
